'use client'

import { useState } from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import ThemeIcon from '@/components/ui/ThemeIcon'
import { ReactCompareSlider } from 'react-compare-slider'
import { getFocalPointStyle } from '@/utilities/getFocalPointStyle'
import { Media } from '@/components/Media'
import type { Media as MediaType } from '@/payload-types'

interface BeforeAfterImage {
  resource?: MediaType | null // Payload media object (preferred)
  src?: string // Fallback for processed data
  alt: string
  focalX?: number | null // Keep for backward compatibility
  focalY?: number | null
}

interface BeforeAfterProps {
  left: BeforeAfterImage
  right: BeforeAfterImage
  className?: string
  heightClass?: string
  leftLabel?: string
  rightLabel?: string
}

export default function BeforeAfter({
  left,
  right,
  className = '',
  heightClass,
  leftLabel = 'VORHER',
  rightLabel = 'NACHHER',
}: BeforeAfterProps) {
  const [position, setPosition] = useState<number>(50)
  const labelCenterLeft = 25
  const labelCenterRight = 75
  const hideRadius = 8
  const showLeft = position > labelCenterLeft + hideRadius
  const showRight = position < labelCenterRight - hideRadius
  return (
    <div className={cn('relative w-full', heightClass || 'aspect-[16/9]', className)}>
      {/* Bottom center labels per half */}
      {(leftLabel || rightLabel) && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex">
          <div className="w-1/2 flex justify-center">
            {leftLabel && (
              <span
                className={cn(
                  'px-3 py-1 bg-brand text-text-white text-sm font-metropolis uppercase transition-opacity duration-200',
                  showLeft ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden={!showLeft}
              >
                {leftLabel}
              </span>
            )}
          </div>
          <div className="w-1/2 flex justify-center">
            {rightLabel && (
              <span
                className={cn(
                  'px-3 py-1 bg-brand text-text-white text-sm font-metropolis uppercase transition-opacity duration-200',
                  showRight ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden={!showRight}
              >
                {rightLabel}
              </span>
            )}
          </div>
        </div>
      )}

      <ReactCompareSlider
        className="w-full h-full relative z-5"
        onlyHandleDraggable
        defaultPosition={50}
        onPositionChange={setPosition}
        itemOne={
          <div className="relative w-full h-full">
            {left.resource ? (
              <Media resource={left.resource} fill imgClassName="object-cover" size="100vw" />
            ) : (
              <Image
                src={left.src || ''}
                alt={left.alt}
                draggable={false}
                fill
                className="object-cover"
                sizes="100vw"
                style={getFocalPointStyle(left.focalX, left.focalY)}
              />
            )}
          </div>
        }
        itemTwo={
          <div className="relative w-full h-full z-5">
            {right.resource ? (
              <Media resource={right.resource} fill imgClassName="object-cover" size="100vw" />
            ) : (
              <Image
                src={right.src || ''}
                alt={right.alt}
                draggable={false}
                fill
                className="object-cover"
                sizes="100vw"
                style={getFocalPointStyle(right.focalX, right.focalY)}
              />
            )}
          </div>
        }
        handle={
          <div className="pointer-events-auto absolute inset-0 flex items-center justify-center">
            {/* Vertical line spanning the full slider height */}
            <div
              className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-white shadow-lg"
              style={{ zIndex: 10 }}
            />
            <div
              aria-label="Schieberegler für Vorher/Nachher"
              className="pointer-events-auto relative flex items-center justify-center cursor-ew-resize"
              style={{
                width: 28,
                height: 28,
                borderRadius: 9999,
                background: 'var(--color-card)',
                border: '2px solid var(--text-white)',
                boxShadow: '0 1px 2px var(--color-shadow)',
                zIndex: 40,
              }}
            >
              <ThemeIcon size="size-4" />
            </div>
          </div>
        }
      />
    </div>
  )
}
